import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PageContent,
  Container,
  Button,
  Field,
  FileInput,
  FormActions,
  FormNotice,
  FormSection,
  FormSummary,
  FormSummaryHeading,
  FormSummaryMeta,
  StatusBadge,
} from 'components';
import { checkPermissions, globalPropTypes } from 'helpers';
import { formatDateRange, formatDateTime } from 'helpers/date';
import { useAccessGuard } from 'hooks';
import {
  loadYcMeetingApplicationCreator,
  saveYcMeetingApplicationCreator,
  removeYcMeetingApplicationCreator,
  downloadMeetingDocumentCreator,
} from 'store/actionsCreators';
import { describeSubmitter } from 'containers/ma/Meetings/Application/submitter';
import { SummaryList } from 'containers/ma/Meetings/Application/Application.styled';
import StatusHistory from 'containers/ma/Meetings/Application/StatusHistory';
import {
  FileList,
  FileRow,
  EmptyNote,
  LinkButton,
  UploadRow,
} from 'containers/yc/Reports/Reports.styled';

/** The blank agreement is offered for download under this name. */
const TEMPLATE_NAME = 'IPMA Confidentiality Agreement.pdf';

/** "2 MB", "512 KB" - the same wording the API answers with. */
const formatSize = (bytes) =>
  bytes >= 1048576
    ? `${Number((bytes / 1048576).toFixed(1))} MB`
    : `${Math.round(bytes / 1024)} KB`;

/**
 * The application of a Young Crew officer to a YC Assembly.
 *
 * There are no questions: the officer downloads the blank confidentiality
 * agreement, fills it in and signs it, and uploads the signed copy. From there
 * the application runs through the same approval and verification as every
 * other one. The same screen renders the read only view of a sent application.
 */
const Application = ({
  meetingType,
  mode,
  data,
  errors,
  userPermissions,
  loadApplication,
  saveApplication,
  clearApplication,
  downloadDocument,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { meetingId, applicationId } = useParams();
  const inputRef = useRef(null);

  // The file picked in this session, uploaded with the next save.
  const [picked, setPicked] = useState(null);
  const [pickError, setPickError] = useState(null);

  const neededRole =
    mode === 'view' ? `yc_meeting_view_${meetingType}` : `yc_meeting_apply_${meetingType}`;
  const [allowed, renderAccessDeniedModal] = useAccessGuard(
    checkPermissions({ roles: [neededRole] }, userPermissions || []),
    '/yc',
    'page.yc.meetings.accessDenied',
  );

  useEffect(() => {
    if (!allowed) return undefined;

    loadApplication({ meetingId, applicationId });

    return () => clearApplication();
  }, [meetingId, applicationId, allowed]);

  // A save that went through uploaded the picked file, so it is no longer
  // waiting to be sent.
  useEffect(() => {
    setPicked(null);
  }, [data]);

  if (!allowed) {
    return <PageContent>{renderAccessDeniedModal()}</PageContent>;
  }

  if (!data) {
    return <PageContent />;
  }

  const { meeting, application, document: stored, documentRules } = data;
  const extensions = (documentRules && documentRules.extensions) || [];
  const maxSize = documentRules ? documentRules.maxSize : null;
  const status = application ? application.status : null;
  const readOnly = mode === 'view' || !data.editable;
  const submittedBy = describeSubmitter(data.submittedBy);
  const backPath = `/yc/meetings/${meetingType}`;

  // A file of the wrong format or size is refused here rather than on save:
  // the server refuses it either way, and finding out on the spot beats a
  // round trip.
  const handlePick = (event) => {
    const file = Array.from(event.target.files || [])[0] || null;

    // The same file has to be pickable again after it was dropped, and the
    // input only fires on a change - so it is emptied after every pick.
    if (inputRef.current) inputRef.current.value = '';

    if (!file) return;

    const extension = (file.name.split('.').pop() || '').toLowerCase();

    if (extensions.length && !extensions.includes(extension)) {
      setPickError(
        t('page.yc.meetings.application.fileWrongFormat', {
          name: file.name,
          formats: extensions.join(', '),
        }),
      );
      return;
    }

    if (maxSize && file.size > maxSize) {
      setPickError(
        t('page.yc.meetings.application.fileTooLarge', {
          name: file.name,
          size: formatSize(maxSize),
        }),
      );
      return;
    }

    setPickError(null);
    setPicked(file);
  };

  const submitForm = (submit) => {
    saveApplication(
      {
        applicationId: application ? application.id : null,
        meetingId: meeting.id,
        meetingType,
        ndaFile: picked,
        submit,
      },
      history,
    );
  };

  const renderActions = () => {
    if (readOnly) {
      return (
        <FormActions>
          <Button
            type="button"
            secondary
            style={{ width: 'auto' }}
            onClick={() => history.push(backPath)}
          >
            {t('page.ma.meetings.application.back')}
          </Button>
        </FormActions>
      );
    }

    return (
      <FormActions>
        {status !== 'SUBMITTED' && (
          <Button
            type="button"
            secondary
            style={{ width: 'auto' }}
            onClick={() => submitForm(false)}
          >
            {t('page.ma.meetings.application.saveDraft')}
          </Button>
        )}
        <Button type="button" style={{ width: 'auto' }} onClick={() => submitForm(true)}>
          {status === 'SUBMITTED'
            ? t('page.ma.meetings.application.saveChanges')
            : t('page.ma.meetings.application.submit')}
        </Button>
        <Button
          type="button"
          secondary
          style={{ width: 'auto' }}
          onClick={() => history.push(backPath)}
        >
          {t('global.cancelButton')}
        </Button>
      </FormActions>
    );
  };

  return (
    <PageContent>
      <Container>
        <form>
          <FormSummary>
            <div>
              <FormSummaryHeading>{meeting.name}</FormSummaryHeading>
              <FormSummaryMeta>
                {t('page.ma.meetings.application.meetingSummary', {
                  date: formatDateRange(meeting.dateFrom, meeting.dateTo),
                  delivery: t(
                    meeting.onsite
                      ? 'page.ma.meetings.application.onsite'
                      : 'page.ma.meetings.application.online',
                  ),
                })}
              </FormSummaryMeta>
            </div>
            {application ? (
              <StatusBadge status={application.status}>{application.statusLabel}</StatusBadge>
            ) : null}
          </FormSummary>

          {data.rejectionReason ? (
            <FormNotice
              type={application && application.status === 'REJECTED' ? 'error' : 'success'}
            >
              {t('page.ma.meetings.application.reason', { reason: data.rejectionReason })}
            </FormNotice>
          ) : null}
          {submittedBy ? (
            <FormSection title={t('page.ipma.meetings.application.applicantSection')}>
              <SummaryList>
                <dt>{t('page.ma.meetings.application.submittedBy')}</dt>
                <dd>{submittedBy}</dd>
                <dt>{t('page.ipma.meetings.application.submittedAt')}</dt>
                <dd>{formatDateTime(application.submittedAt) || '-'}</dd>
              </SummaryList>
            </FormSection>
          ) : null}
          {!data.editable && !readOnly ? (
            <FormNotice type="warning">{t('page.ma.meetings.application.closed')}</FormNotice>
          ) : null}

          <FormSection
            title={t('page.yc.meetings.application.ndaSection')}
            description={readOnly ? undefined : t('page.yc.meetings.application.ndaHelp')}
          >
            <Button
              type="button"
              secondary
              style={{ width: 'auto', marginBottom: '2rem' }}
              onClick={() =>
                downloadDocument({ kind: 'template', type: meetingType, name: TEMPLATE_NAME })
              }
            >
              {t('page.yc.meetings.application.downloadTemplate')}
            </Button>

            <Field
              id="document"
              label={t('page.yc.meetings.application.signedNda')}
              description={readOnly ? undefined : t('page.yc.meetings.application.signedNdaHelp')}
              required={!readOnly}
              error={errors.document || pickError}
            >
              <div>
                {!readOnly ? (
                  <UploadRow>
                    <FileInput
                      id="document"
                      ref={inputRef}
                      accept={extensions.map((extension) => `.${extension}`).join(',')}
                      onChange={handlePick}
                      clear={() => setPicked(null)}
                      placeholder={t('page.yc.meetings.application.filePlaceholder')}
                    />
                  </UploadRow>
                ) : null}

                {stored || picked ? (
                  <FileList>
                    {stored ? (
                      <FileRow>
                        <LinkButton
                          type="button"
                          onClick={() =>
                            downloadDocument({ kind: 'yc', id: application.id, name: stored.name })
                          }
                        >
                          {stored.name}
                        </LinkButton>
                        {picked ? (
                          <span>{t('page.yc.meetings.application.replaceNda')}</span>
                        ) : null}
                      </FileRow>
                    ) : null}
                    {picked ? (
                      <FileRow>
                        <span>
                          {t('page.yc.meetings.application.pendingNda', { name: picked.name })}
                        </span>
                        <LinkButton type="button" onClick={() => setPicked(null)}>
                          {t('global.removeButton')}
                        </LinkButton>
                      </FileRow>
                    ) : null}
                  </FileList>
                ) : (
                  <EmptyNote>{t('page.yc.meetings.application.noNda')}</EmptyNote>
                )}
              </div>
            </Field>
          </FormSection>

          {renderActions()}

          {application && <StatusHistory items={data.statusHistory} />}
        </form>
      </Container>
    </PageContent>
  );
};

Application.propTypes = {
  meetingType: PropTypes.oneOf(['ycnda']).isRequired,
  mode: PropTypes.oneOf(['create', 'edit', 'view']).isRequired,
  userPermissions: globalPropTypes.UserPermissions.isRequired,
  data: PropTypes.shape({
    meeting: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      type: PropTypes.string,
      onsite: PropTypes.bool,
      dateFrom: PropTypes.string,
      dateTo: PropTypes.string,
    }),
    application: PropTypes.shape({
      id: PropTypes.number,
      status: PropTypes.string,
      statusLabel: PropTypes.string,
      submittedAt: PropTypes.string,
      verified: PropTypes.bool,
      flag: PropTypes.string,
    }),
    document: PropTypes.shape({
      name: PropTypes.string,
    }),
    documentRules: PropTypes.shape({
      extensions: PropTypes.arrayOf(PropTypes.string),
      maxSize: PropTypes.number,
    }),
    editable: PropTypes.bool,
    rejectionReason: PropTypes.string,
    statusHistory: PropTypes.arrayOf(PropTypes.shape({})),
    submittedBy: PropTypes.shape({
      name: PropTypes.string,
      organization: PropTypes.string,
      role: PropTypes.string,
    }),
  }),
  errors: PropTypes.objectOf(PropTypes.string),
  loadApplication: PropTypes.func.isRequired,
  saveApplication: PropTypes.func.isRequired,
  clearApplication: PropTypes.func.isRequired,
  downloadDocument: PropTypes.func.isRequired,
};

Application.defaultProps = {
  data: null,
  errors: {},
};

const mapStateToProps = ({ yc, user }) => ({
  data: yc.meetings.application.data,
  errors: yc.meetings.application.errors,
  userPermissions: user.userPermissions,
});

const mapDispatchToProps = (dispatch) => ({
  loadApplication: (payload) => dispatch(loadYcMeetingApplicationCreator(payload)),
  saveApplication: (payload, history) =>
    dispatch(saveYcMeetingApplicationCreator(payload, history)),
  clearApplication: () => dispatch(removeYcMeetingApplicationCreator()),
  downloadDocument: (payload) => dispatch(downloadMeetingDocumentCreator(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Application);
